import React, { FC, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Popover } from "@evergis/ui";
import { GeometryType } from "@evergis/api";

import { LegendButton, MapLegendContainer } from "./styled";
import { LegendLayer } from "./LegendLayer";
import { checkLayerVisibility } from "../../../../utils/layers";
import { getGeometrySelection } from "../../../../ducks/geometrySelection/selectors";
import { getBottomHeight, getBottomState } from "../../../../ducks/layout/selectors";
import { toggleMapLegend } from "../../../../ducks/layout/layout";
import { combineLayers, useProjectLayerInfos, useProjectLayers } from "../../../../hooks/project/useProjectLayers";
import { useNirvanaMode } from "../../../../hooks/layout/useNirvanaMode";
import { useWidgetConfiguration } from "../../../Dashboard/hooks/useWidgetConfiguration";
import { useIsDashboard } from "../../../../hooks/location/useIsDashboard";
import { useDashboardLayers } from "../../../../hooks/project/useDashboardLayers";
import { WidgetType } from "../../../FeatureCard/types";

export const Legend: FC<{ showLegend: boolean; toggleLegend: VoidFunction }> = ({ showLegend, toggleLegend }) => {
  const { t } = useTranslation("mapControls");
  const dispatch = useDispatch();
  const { active } = useSelector(getGeometrySelection);
  const layers = useProjectLayers();
  const layerInfos = useProjectLayerInfos();
  const { isNirvanaMode } = useNirvanaMode();
  const bottomState = useSelector(getBottomState);
  const bottomHeight = useSelector(getBottomHeight);
  const isDashboard = useIsDashboard();
  const { layers: dashLayers } = useDashboardLayers();
  const { currentConfig } = useWidgetConfiguration(WidgetType.Dashboard);
  const ref = useRef();

  const combinedLayers = useMemo(() => combineLayers(layers, { savePath: true }), [layers]);

  const geometryLayers = useMemo(() => {
    return combinedLayers.filter(
      ({ name }) =>
        layerInfos.layerInfos?.find(info => info.name === name)?.geometryType !== GeometryType.Unknown &&
        (!isDashboard || !currentConfig.layers?.length || currentConfig.layers?.some(layer => layer.name === name)),
    );
  }, [combinedLayers, isDashboard, currentConfig.layers, layerInfos.layerInfos]);

  useEffect(() => {
    if (showLegend && !geometryLayers.length) {
      dispatch(toggleMapLegend());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometryLayers.length]);

  const prevIsNirvanaMode = useRef(isNirvanaMode);

  useEffect(() => {
    if (showLegend && prevIsNirvanaMode.current !== isNirvanaMode) {
      dispatch(toggleMapLegend());

      setTimeout(() => {
        dispatch(toggleMapLegend());
      }, 500);
    }
    prevIsNirvanaMode.current = isNirvanaMode;
  }, [isNirvanaMode]);

  if (!geometryLayers.length) {
    return null;
  }

  return (
    <Popover
      zIndex={2}
      anchor={<LegendButton title={t("legend", { ns: "print" })} primary={showLegend} onClick={toggleLegend} />}
      open={showLegend}
    >
      <MapLegendContainer
        geometrySelection={active}
        bottomHeight={bottomState === "opened" ? bottomHeight : 0}
        ref={ref}
      >
        {geometryLayers.map(layer => (
          <LegendLayer
            key={layer.name}
            item={{ ...layer, isVisible: isDashboard ? checkLayerVisibility(dashLayers[layer.name]) : layer.isVisible }}
          />
        ))}
      </MapLegendContainer>
    </Popover>
  );
};
